// AI 深度全局状态:App 顶层挂载(AuthProvider 之内),登录后 GET /api/settings 初始化。
// 两层档位:defaultDepth(账户默认,存服务端)+ sessionDepth(临时改档,仅本次会话,内存态,
// null = 跟随默认)。effectiveDepth = sessionDepth ?? defaultDepth,并同步到 lib/aiDepth 的
// 模块级快照,供 toolsApi/platformApi/narrativeApi 等非 React 层在请求体里带上 ai_depth。
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  clampDepth,
  FALLBACK_TIERS,
  normalizeTiers,
  settingsApi,
  syncEffectiveDepthSnapshot,
  type DepthTier,
} from "@/lib/aiDepth";
import { useAuth } from "@/state/AuthContext";

interface AiDepthState {
  /** 账户默认档位(个人设置页保存,服务端持久化) */
  defaultDepth: number;
  /** 临时改档(CostBadge popover 设置);null = 跟随默认。仅本次会话,不落盘。 */
  sessionDepth: number | null;
  /** 实际生效档位 = sessionDepth ?? defaultDepth */
  effectiveDepth: number;
  /** 五档定义(label/desc/multiplier);后端返回前用 FALLBACK_TIERS 兜底 */
  tiers: DepthTier[];
  setSessionDepth: (d: number | null) => void;
  /** 保存默认档(乐观更新,失败回滚并返回错误文案);成功会顺带清掉临时改档 */
  saveDefaultDepth: (d: number) => Promise<{ ok: boolean; error?: string }>;
}

const AiDepthCtx = createContext<AiDepthState | null>(null);

export function AiDepthProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [defaultDepth, setDefaultDepth] = useState(1);
  const [sessionDepth, setSessionDepthState] = useState<number | null>(null);
  const [tiers, setTiers] = useState<DepthTier[]>(FALLBACK_TIERS);

  const effectiveDepth = clampDepth(sessionDepth ?? defaultDepth);

  // 非 React 层(api 函数)靠模块级快照拿当前档位——每次生效值变化都同步过去。
  useEffect(() => {
    syncEffectiveDepthSnapshot(effectiveDepth);
  }, [effectiveDepth]);

  // 登录态就绪后拉一次设置;失败(网络/后端未升级 404)静默保持默认 1 档,不打扰用户。
  // 换账号 / 登出重登会按新 user.id 重拉并清掉上个会话的临时改档。
  const userId = auth.user?.id ?? null;
  useEffect(() => {
    setSessionDepthState(null);
    if (!userId) {
      setDefaultDepth(1);
      setTiers(FALLBACK_TIERS);
      return;
    }
    let cancelled = false;
    settingsApi.get().then((res) => {
      if (cancelled || !res.ok || !res.data?.ok) return;
      setDefaultDepth(clampDepth(Number(res.data.settings?.ai_depth ?? 1)));
      setTiers(normalizeTiers(res.data.depth_tiers));
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const setSessionDepth = useCallback((d: number | null) => {
    setSessionDepthState(d === null ? null : clampDepth(d));
  }, []);

  const saveDefaultDepth = useCallback(
    async (d: number): Promise<{ ok: boolean; error?: string }> => {
      const next = clampDepth(d);
      let prev = 1;
      setDefaultDepth((cur) => {
        prev = cur; // 乐观更新前记住旧值,失败回滚
        return next;
      });
      setSessionDepthState(null); // 用户明确选了新默认 → 临时改档随之作废
      const res = await settingsApi.patchAiDepth(next);
      if (!res.ok || !res.data?.ok) {
        setDefaultDepth(prev);
        return { ok: false, error: !res.ok ? res.error : "保存失败,请稍后重试" };
      }
      // 以服务端回执为准(含可能更新的档位表)
      setDefaultDepth(clampDepth(Number(res.data.settings?.ai_depth ?? next)));
      if (res.data.depth_tiers) setTiers(normalizeTiers(res.data.depth_tiers));
      return { ok: true };
    },
    []
  );

  const value = useMemo(
    () => ({ defaultDepth, sessionDepth, effectiveDepth, tiers, setSessionDepth, saveDefaultDepth }),
    [defaultDepth, sessionDepth, effectiveDepth, tiers, setSessionDepth, saveDefaultDepth]
  );

  return <AiDepthCtx.Provider value={value}>{children}</AiDepthCtx.Provider>;
}

// eslint-disable-next-line react/only-export-components -- 与 AuthContext/ToastContext 同款"Provider+hook 同文件"约定
export function useAiDepth(): AiDepthState {
  const ctx = useContext(AiDepthCtx);
  if (!ctx) throw new Error("useAiDepth 必须在 AiDepthProvider 内使用");
  return ctx;
}
